/* @jsx h */
import { defineComponent, h, onMounted, ref, watch } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { LOW_MARGIN_PCT } from "@app/vue/pages/CatalogEditor/components/RecipeEditor/costColumns";
import { VarianceTable, type VarianceReport } from "./VarianceTable";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

type Report = {
  totals: {
    revenue_net: number;
    cost: number;
    margin: number;
    costed_revenue: number;
    costed_share: number | null;
    cost_pct: number | null;
  };
  products: {
    product_id: number;
    name: string | null;
    quantity: number;
    revenue_net: number;
    cost: number;
    margin: number;
    margin_pct: number | null;
    has_recipe: boolean;
  }[];
};

const PERIODS = [1, 7, 30, 90] as const;

// What each dish sold for and cost over a period, from the cost recorded
// with every payment. See docs/plans/inventory_and_costing/04_costing.md (#6).
export const InventoryMarginsPage = defineComponent({
  name: "InventoryMarginsPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const days = ref<(typeof PERIODS)[number]>(7);
    const report = ref<Report | null>(null);
    const variance = ref<VarianceReport | null>(null);

    const load = async () => {
      loading.value = true;
      try {
        const to = new Date();
        const from = new Date(to.getTime() - days.value * 86_400_000);
        const range = { from: from.toISOString(), to: to.toISOString() };
        const [margins, usage] = await Promise.all([
          trpc.stock.marginReport.query(range),
          trpc.stock.varianceReport.query(range),
        ]);
        report.value = margins as Report;
        variance.value = usage as VarianceReport;
      } catch {
        toast.showToast(t("inventory.margins.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };

    onMounted(load);
    watch(days, load);

    const stat = (labelKey: string, value: string, hint?: string) => (
      <div class="rounded-lg border border-base-300 bg-base-100 px-4 py-3">
        <div class="text-xs text-base-content/60">{t(labelKey)}</div>
        <div class="text-xl font-semibold">{value}</div>
        {hint && <div class="text-xs text-base-content/50">{hint}</div>}
      </div>
    );

    const pct = (v: number | null) => (v == null ? "—" : `${v.toFixed(1)}%`);

    return () => {
      const r = report.value;
      return (
        <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
          <SubpageHeader
            title={t("inventory.nav.margins")}
            subtitle={t("inventory.margins.subtitle")}
          >
            <div class="join">
              {PERIODS.map((d) => (
                <button
                  key={d}
                  class={[
                    "btn btn-sm join-item",
                    days.value === d ? "btn-primary" : "btn-ghost",
                  ].join(" ")}
                  onClick={() => (days.value = d)}
                >
                  {t("inventory.waste.last_days").replace("{days}", String(d))}
                </button>
              ))}
            </div>
          </SubpageHeader>

          {loading.value || !r ? (
            <div class="flex flex-1 items-center justify-center">
              <Loading />
            </div>
          ) : (
            <div class="flex-1 space-y-3 overflow-auto p-3">
              <div class="grid grid-cols-2 gap-2 md:grid-cols-5">
                {stat(
                  "inventory.margins.revenue",
                  r.totals.revenue_net.toFixed(2),
                  t("inventory.purchases.excl_vat"),
                )}
                {stat("inventory.margins.cost", r.totals.cost.toFixed(2))}
                {stat("inventory.margins.margin", r.totals.margin.toFixed(2))}
                {stat(
                  "inventory.margins.cost_pct",
                  pct(r.totals.cost_pct),
                  t("inventory.margins.cost_pct_hint"),
                )}
                {stat(
                  "inventory.margins.costed_share",
                  pct(r.totals.costed_share),
                  t("inventory.margins.costed_share_hint"),
                )}
              </div>

              {variance.value && <VarianceTable report={variance.value} />}

              <h3 class="pt-2 text-base font-semibold">
                {t("inventory.margins.by_dish")}
              </h3>
              {r.products.length === 0 ? (
                <p class="p-3 text-sm text-base-content/60">
                  {t("inventory.margins.empty")}
                </p>
              ) : (
                <div class="overflow-x-auto rounded-lg border border-base-300 bg-base-100">
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th>{t("inventory.margins.dish")}</th>
                        <th class="text-right">
                          {t("inventory.margins.sold")}
                        </th>
                        <th class="text-right">
                          {t("inventory.margins.revenue")}
                        </th>
                        <th class="text-right">
                          {t("inventory.margins.cost")}
                        </th>
                        <th class="text-right">
                          {t("inventory.margins.margin")}
                        </th>
                        <th class="text-right">
                          {t("inventory.recipe.col_margin")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.products.map((p) => (
                        <tr key={p.product_id}>
                          <td>
                            {p.name ?? `#${p.product_id}`}
                            {!p.has_recipe && (
                              <span class="badge badge-ghost badge-sm ml-2">
                                {t("inventory.margins.no_recipe")}
                              </span>
                            )}
                          </td>
                          <td class="text-right">{p.quantity}</td>
                          <td class="text-right">{p.revenue_net.toFixed(2)}</td>
                          <td class="text-right">
                            {p.has_recipe ? p.cost.toFixed(2) : "—"}
                          </td>
                          <td class="text-right">
                            {p.has_recipe ? p.margin.toFixed(2) : "—"}
                          </td>
                          <td
                            class={[
                              "text-right font-medium",
                              p.margin_pct == null
                                ? ""
                                : p.margin_pct < LOW_MARGIN_PCT
                                  ? "text-warning"
                                  : "text-success",
                            ].join(" ")}
                          >
                            {pct(p.margin_pct)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      );
    };
  },
});
